import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Check, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_AMOUNTS = {
  'promo-monthly': '1.99',
  'promo-yearly': '20.00',
  'monthly': '4.99',
  'yearly': '50.00',
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [paypalClientId, setPaypalClientId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPayPalConfig();
  }, []);

  const fetchPayPalConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/paypal`);
      setPaypalClientId(response.data.client_id);
    } catch (error) {
      console.error('Failed to fetch PayPal config:', error);
    }
  };

  const handlePlanSelect = (planId) => {
    if (!user) {
      toast.info('Please login to subscribe');
      navigate('/login');
      return;
    }
    if (user.subscription_status === 'active') {
      toast.info('You already have an active subscription!');
      return;
    }
    setSelectedPlan(planId);
  };

  const handleApprove = async (data, planId) => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/subscription/paypal`,
        {
          plan_id: planId,
          paypal_subscription_id: data.orderID
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription activated! Welcome to premium.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to activate subscription. Please contact support.');
    } finally {
      setProcessing(false);
    }
  };

  const plans = [
    {
      id: 'promo-monthly',
      name: 'Launch Special - Monthly',
      price: '$1.99',
      period: '/month',
      badge: 'First 100 Only!',
      isPromo: true,
      features: [
        'Limited to first 100 subscribers',
        'Lock in $1.99/month rate FOR LIFE',
        'Access to all 128+ premium episodes',
        'Private live broadcasts',
        'Ad-free viewing experience',
        'Email notifications',
        'Cancel anytime'
      ]
    },
    {
      id: 'promo-yearly',
      name: 'Launch Special - Yearly',
      price: '$20',
      period: '/year',
      badge: 'Best Value - First 100!',
      isPromo: true,
      features: [
        'Limited to first 100 subscribers',
        'Lock in $20/year rate FOR LIFE',
        'All Monthly features included',
        'Save $3.88 vs monthly',
        'Priority support',
        'Exclusive community access',
        'Behind-the-scenes content'
      ]
    },
    {
      id: 'monthly',
      name: 'Monthly',
      price: '$4.99',
      period: '/month',
      features: [
        'Access to all 128+ premium episodes',
        'Private live broadcasts',
        'Ad-free viewing experience',
        'Email notifications',
        'Early access to new content',
        'Support independent journalism',
        'Cancel anytime'
      ]
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: '$50',
      period: '/year',
      badge: 'Save $10',
      features: [
        'All Monthly features included',
        'Save $10 per year',
        'Priority support',
        'Exclusive community access',
        'Behind-the-scenes content',
        'Best value for committed viewers'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        {/* Limited Time Banner */}
        <div className="mb-12 bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-sm text-center border-2 border-amber-500">
          <div className="inline-block mb-2 px-4 py-1 bg-amber-500 text-slate-950 text-sm font-bold rounded-sm animate-pulse" style={{ fontFamily: 'Oswald, sans-serif' }}>
            LIMITED TIME OFFER
          </div>
          <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
            First 100 Subscribers Get LIFETIME Pricing!
          </h2>
          <p className="text-xl text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            Lock in $1.99/month or $20/year forever. No code needed — just click and pay!
          </p>
        </div>

        <div className="text-center mb-16">
          <h1 
            data-testid="pricing-heading"
            className="text-4xl sm:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            Choose Your Plan
          </h1>
          <p className="text-xl text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
            Support truth-telling journalism and get access to exclusive content.
          </p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Shield className="w-4 h-4 text-green-500" />
            <p className="text-sm text-green-400" style={{ fontFamily: 'Inter, sans-serif' }}>
              No promo code needed — promotional pricing is applied automatically
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              data-testid={`pricing-plan-${plan.id}`}
              className={`relative p-8 rounded-sm ${
                plan.isPromo 
                  ? 'bg-gradient-to-br from-amber-500/20 to-red-600/20 border-2 border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.3)]' 
                  : 'bg-slate-900 border border-slate-800 hover:border-red-600'
              }`}
              style={{ transition: 'border 0.3s' }}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-amber-500 text-slate-950 px-4 py-1 text-sm font-bold rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {plan.badge}
                  </span>
                </div>
              )}
              
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
                {plan.name}
              </h3>
              <div className="mb-6">
                <span className="text-5xl font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{plan.price}</span>
                <span className="text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>{plan.period}</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300" style={{ fontFamily: 'Inter, sans-serif' }}>{feature}</span>
                  </li>
                ))}
              </ul>
              
              {!user ? (
                <Button 
                  data-testid={`select-plan-${plan.id}`}
                  onClick={() => handlePlanSelect(plan.id)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white h-12 rounded-sm font-bold"
                  style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
                >
                  Select Plan
                </Button>
              ) : selectedPlan === plan.id && paypalClientId ? (
                <div data-testid={`paypal-buttons-${plan.id}`}>
                  {processing ? (
                    <div className="text-center py-4 text-amber-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Processing your payment...
                    </div>
                  ) : (
                    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD', intent: 'capture' }}>
                      <PayPalButtons
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: {
                                value: PLAN_AMOUNTS[plan.id],
                                currency_code: 'USD'
                              },
                              description: `Unfiltered Vision News - ${plan.name}`
                            }]
                          });
                        }}
                        onApprove={(data, actions) => {
                          return actions.order.capture().then(() => {
                            handleApprove(data, plan.id);
                          });
                        }}
                        onError={(err) => {
                          console.error('PayPal Error:', err);
                          toast.error('Payment failed. Please try again.');
                        }}
                        onCancel={() => {
                          toast.info('Payment cancelled.');
                          setSelectedPlan(null);
                        }}
                        style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                      />
                    </PayPalScriptProvider>
                  )}
                  <button 
                    data-testid={`cancel-plan-${plan.id}`}
                    onClick={() => setSelectedPlan(null)}
                    className="w-full mt-2 text-sm text-slate-400 hover:text-white transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Choose a different plan
                  </button>
                </div>
              ) : (
                <Button 
                  data-testid={`select-plan-${plan.id}`}
                  onClick={() => handlePlanSelect(plan.id)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white h-12 rounded-sm font-bold"
                  style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
                >
                  {user?.subscription_status === 'active' ? 'Already Subscribed' : 'Select Plan'}
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 text-center text-slate-400">
          <p style={{ fontFamily: 'Inter, sans-serif' }}>
            All subscriptions can be cancelled at any time. Questions? Contact us at support@unfilteredvisionnews.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
